import type { Translate, TranslationKey } from '@/shared/i18n';

import { PlanCategory } from '@/shared/types/Billing';

import type { FeatureTooltipItem } from '../ui/FeatureTooltip';

const ACTION_KEYS: Record<string, TranslationKey> = {
  Downgrade: 'pricing.actions.downgrade',
  'Get started': 'pricing.actions.getStarted',
  Renew: 'pricing.actions.renew',
  'Switch to annual': 'pricing.actions.switchAnnual',
  'Switch to monthly': 'pricing.actions.switchMonthly',
  Upgrade: 'pricing.actions.upgrade',
};

const FEATURE_KEYS: Record<string, TranslationKey> = {
  aiRewriter: 'pricing.feature.aiRewriter',
  allBusiness: 'pricing.feature.allBusiness',
  allCreator: 'pricing.feature.allCreator',
  allCreatorPro: 'pricing.feature.allCreatorPro',
  api: 'pricing.feature.api',
  applyChangesForFree: 'pricing.feature.applyChanges',
  businessContract: 'pricing.feature.businessContract',
  customerSuccessManager: 'pricing.feature.customerSuccess',
  customVoiceClone: 'pricing.feature.voiceLibrary',
  customVoiceCloneBusiness: 'pricing.feature.voiceClones10',
  customVoiceCloneEnterprise: 'pricing.price.custom',
  customVoiceClonePro: 'pricing.feature.voiceClones3',
  customVoiceSettings: 'pricing.feature.voiceSettings',
  flexibleMinutes: 'pricing.feature.flexibleMinutes',
  humanInTheLoop: 'pricing.feature.humanReview',
  lipSync: 'pricing.feature.lipSync',
  machineTranslation: 'pricing.feature.machineTranslation',
  multiLanguage: 'pricing.feature.multiLanguage',
  noWatermark: 'pricing.feature.noWatermark',
  priorityProcessing: 'pricing.feature.priority',
  screenRecording: 'pricing.plan.unlimitedRecording',
  speechToText: 'pricing.feature.speechToText',
  srtDownload: 'pricing.feature.srtDownload',
  srtDownloadUpload: 'pricing.feature.srtDownloadUpload',
  srtUpload: 'pricing.feature.srtUpload',
  support247: 'pricing.feature.support',
  teamspaces: 'pricing.feature.teamspaces',
  teamspacesBusiness: 'pricing.feature.teamspace5',
  teamspacesCreatorPro: 'pricing.feature.teamspace3',
  teamspacesEnterprise: 'pricing.price.custom',
  translate: 'pricing.feature.languages',
  translationDictionary: 'pricing.feature.dictionary',
  userPrompting: 'pricing.feature.prompting',
  videoCaptions: 'pricing.feature.captions',
  videoEditor: 'pricing.feature.editor',
  voiceCloning: 'pricing.feature.voiceCloning',
  watermark: 'pricing.feature.watermark',
  onlyVideoDownload: 'pricing.feature.onlyVideoDownload',
};

const FEATURE_TOOLTIP_KEYS: Record<string, { descriptionKey?: TranslationKey; titleKey: TranslationKey }> = {
  aiRewriter: { titleKey: 'pricing.compare.row.controlsDescription' },
  api: { titleKey: 'pricing.compare.row.apiDescription' },
  applyChangesForFree: { titleKey: 'pricing.compare.row.controlsDescription' },
  customVoiceClone: {
    descriptionKey: 'pricing.compare.row.voicesDescription',
    titleKey: 'pricing.compare.row.voices',
  },
  customVoiceCloneBusiness: {
    descriptionKey: 'pricing.compare.row.voicesDescription',
    titleKey: 'pricing.compare.row.voices',
  },
  customVoiceCloneEnterprise: {
    descriptionKey: 'pricing.compare.row.voicesDescription',
    titleKey: 'pricing.compare.row.voices',
  },
  customVoiceClonePro: {
    descriptionKey: 'pricing.compare.row.voicesDescription',
    titleKey: 'pricing.compare.row.voices',
  },
  customVoiceSettings: {
    descriptionKey: 'pricing.compare.row.controlsDescription',
    titleKey: 'pricing.compare.row.controls',
  },
  humanInTheLoop: {
    descriptionKey: 'pricing.compare.row.reviewDescription',
    titleKey: 'pricing.compare.row.review',
  },
  lipSync: {
    descriptionKey: 'pricing.compare.row.lipSyncDescription',
    titleKey: 'pricing.compare.row.lipSync',
  },
  multiLanguage: { titleKey: 'pricing.compare.row.multiLanguage' },
  srtDownload: { titleKey: 'pricing.compare.row.srtDescription' },
  srtDownloadUpload: { titleKey: 'pricing.compare.row.srtDescription' },
  srtUpload: { titleKey: 'pricing.compare.row.srtDescription' },
  teamspacesBusiness: { titleKey: 'pricing.compare.row.teamDescription' },
  teamspacesCreatorPro: { titleKey: 'pricing.compare.row.teamDescription' },
  teamspacesEnterprise: { titleKey: 'pricing.compare.row.teamDescription' },
  translate: {
    descriptionKey: 'pricing.feature.languagesDescription',
    titleKey: 'pricing.feature.languages',
  },
  translationDictionary: { titleKey: 'pricing.compare.row.glossaryDescription' },
  userPrompting: {
    descriptionKey: 'pricing.compare.row.promptingDescription',
    titleKey: 'pricing.compare.row.prompting',
  },
  videoCaptions: { titleKey: 'pricing.compare.row.controlsDescription' },
  videoEditor: { titleKey: 'pricing.compare.row.controlsDescription' },
  voiceCloning: {
    descriptionKey: 'pricing.feature.voiceCloningDescription',
    titleKey: 'pricing.feature.voiceCloning',
  },
};

const PLAN_NOTE_KEYS: Record<PlanCategory, TranslationKey> = {
  [PlanCategory.Business]: 'pricing.plan.bestValue',
  [PlanCategory.Creator]: 'pricing.plan.bestForIndividuals',
  [PlanCategory.CreatorPro]: 'pricing.plan.mostPopular',
  [PlanCategory.Enterprise]: 'pricing.plan.tailoredOnboarding',
  [PlanCategory.Starter]: 'pricing.plan.bestForBeginners',
};

export const getPricingActionLabel = (t: Translate, action: string) => {
  const key = ACTION_KEYS[action];
  return key ? t(key) : action;
};

export const getPricingFeatureLabel = (t: Translate, featureKey: string, fallback = '') => {
  const translationKey = FEATURE_KEYS[featureKey];
  return translationKey ? t(translationKey) : fallback;
};

export const getPricingPlanNoteLabel = (t: Translate, category: PlanCategory) => t(PLAN_NOTE_KEYS[category]);

export const getPricingFeatureTooltip = (
  t: Translate,
  featureKey: string,
  tooltip: FeatureTooltipItem | undefined,
): FeatureTooltipItem | undefined => {
  if (!tooltip) return undefined;

  const tooltipKeys = FEATURE_TOOLTIP_KEYS[featureKey];
  return {
    ...tooltip,
    title: tooltipKeys ? t(tooltipKeys.titleKey) : getPricingFeatureLabel(t, featureKey, tooltip.title),
    description: tooltipKeys?.descriptionKey ? t(tooltipKeys.descriptionKey) : tooltip.description,
    linkTitle: tooltip.linkTitle ? t('pricing.feature.readDocs') : undefined,
  };
};
